import { LocalDate } from '@js-joda/core';
import isNil from 'lodash/isNil';
import { useEffect } from 'react';

import { NumberPatternType, TranslatableText } from '@dreryk/react-common';
import { DateRangePickerLegacy, NumberInput, StyledFlex, StyledMaxWidth, Textarea } from '@dreryk/react-components';
import { Range } from '@dreryk/watermelon';

import { commonTranslationKeys } from '../../../../../translations';
import { MedicalLeaveFormSectionColumn } from '../../MedicalLeaveFormSectionColumn/MedicalLeaveFormSectionColumn';
import { StyledMedicalLeaveFormReason } from '../../StyledMedicalLeaveFormReason';
import { MAX_LEAVE_DAYS, MEDICAL_LEAVE_REASON_THRESHOLD, MIN_LEAVE_DAYS } from '../../common.constants';
import { MedicalLeaveCommonFormController } from '../../common.types';
import { getTodayDate } from '../../common.utils';
import { MedicalLeaveFormSection } from '../MedicalLeaveFormSection';

import { areDatesAboveMaxLeaveDays, findStartDate, getDatesDaysDiff } from './leaveDateRange.utils';
import { useLeaveDaysCountControls, useMedicalLeaveEndDateAdjuster } from './leaveDateRangeSection.hooks';

type MedicalLeaveFormLeaveDurationSectionProps = {
   readonly controller: MedicalLeaveCommonFormController;
   readonly draftValuesReason?: string;
   readonly reasonFieldHidden?: boolean;
   readonly title: TranslatableText;
};

const isDateAboveMaxLeaveDays = (dateRange: Range<LocalDate>) => (date: LocalDate): boolean =>
   areDatesAboveMaxLeaveDays(findStartDate(dateRange) || getTodayDate(), date);

const REASON_FIELD_MAX_LENGTH = 3000;

// eslint-disable-next-line max-lines-per-function
const MedicalLeaveFormLeaveDateRangeSection = (props: MedicalLeaveFormLeaveDurationSectionProps): JSX.Element => {
   const { controller } = props;
   useLeaveDaysCountControls(controller);

   const dateRange = controller.watch('leaveDateRange');
   const adjustEndDate = useMedicalLeaveEndDateAdjuster(controller);
   const shouldDisplayReasonField =
      !props.reasonFieldHidden &&
      dateRange &&
      dateRange.hasLowerBound() &&
      getDatesDaysDiff(dateRange.lowerEndpoint(), getTodayDate()) > MEDICAL_LEAVE_REASON_THRESHOLD;

   const { draftValuesReason } = props;
   useEffect(() => {
      if (shouldDisplayReasonField && !isNil(draftValuesReason) && isNil(controller.getValue('reason'))) {
         controller.setValue('reason', draftValuesReason);
      }
   }, [draftValuesReason, controller, shouldDisplayReasonField]);

   return (
      <MedicalLeaveFormSection title={props.title}>
         <StyledFlex direction={'column'} gap={'20px'}>
            <StyledFlex gap='20px'>
               <MedicalLeaveFormSectionColumn maxWidth={280}>
                  <DateRangePickerLegacy
                     controller={props.controller.register('leaveDateRange', { required: true })}
                     invalidDateRangeMessage={commonTranslationKeys.invalidDateRange}
                     isOutsideRange={dateRange ? isDateAboveMaxLeaveDays(dateRange) : undefined}
                     onBlur={adjustEndDate}
                     label={commonTranslationKeys.periodOfInabilityToWork}
                     required
                     testId={'leaveDateRange'}
                  />
               </MedicalLeaveFormSectionColumn>
               <MedicalLeaveFormSectionColumn title={commonTranslationKeys.leaveDaysCount}>
                  <StyledMaxWidth maxWidth={80}>
                     <NumberInput
                        controller={props.controller.register('leaveDaysCount', { required: true })}
                        min={MIN_LEAVE_DAYS}
                        max={MAX_LEAVE_DAYS}
                        testId={'leaveDaysCount'}
                        pattern={NumberPatternType.POSITIVE_INTEGER}
                     />
                  </StyledMaxWidth>
               </MedicalLeaveFormSectionColumn>
            </StyledFlex>
            {shouldDisplayReasonField ? (
               <StyledMedicalLeaveFormReason>
                  <Textarea
                     controller={controller.register('reason', { required: true })}
                     label={commonTranslationKeys.reason}
                     placeholder={commonTranslationKeys.leaveDateRangeReason}
                     maxLength={REASON_FIELD_MAX_LENGTH}
                     testId='reason'
                     fillParent
                  />
               </StyledMedicalLeaveFormReason>
            ) : null}
         </StyledFlex>
      </MedicalLeaveFormSection>
   );
};

export { MedicalLeaveFormLeaveDateRangeSection };
